import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { monthOptions, quarterOptions, yearOptions } from "@/lib/periods";
import { todayNassau } from "@/lib/format";

export function PeriodSelector({ onChange }) {
  const today = todayNassau();
  const opts = useMemo(() => ({ month: monthOptions(today), quarter: quarterOptions(today), year: yearOptions(today) }), [today]);
  const [mode, setMode] = useState("month");
  const [value, setValue] = useState(opts.month[0].value);
  const [custom, setCustom] = useState({ start: opts.month[0].start, end: today });
  const [error, setError] = useState("");

  useEffect(() => {
    if (mode === "custom") return;
    const o = opts[mode].find((x) => x.value === value);
    if (o) onChange({ start: o.start, end: o.end, label: o.label });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, value]);

  const changeMode = (m) => {
    setMode(m);
    setError("");
    if (m !== "custom") setValue(opts[m][0].value);
  };

  const applyCustom = () => {
    if (!custom.start || !custom.end || custom.end < custom.start) return setError("End date must be on or after start date");
    setError("");
    onChange({ start: custom.start, end: custom.end, label: "Custom range" });
  };

  return (
    <div className="flex flex-wrap items-end gap-2" data-testid="period-selector">
      <Select value={mode} onValueChange={changeMode}>
        <SelectTrigger className="w-[130px] h-10 rounded-xl bg-card" data-testid="period-mode-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="month" data-testid="period-mode-month">Month</SelectItem>
          <SelectItem value="quarter" data-testid="period-mode-quarter">Quarter</SelectItem>
          <SelectItem value="year" data-testid="period-mode-year">Year</SelectItem>
          <SelectItem value="custom" data-testid="period-mode-custom">Custom range</SelectItem>
        </SelectContent>
      </Select>
      {mode !== "custom" ? (
        <Select value={value} onValueChange={setValue}>
          <SelectTrigger className="w-[200px] h-10 rounded-xl bg-card" data-testid="period-value-select"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">
            {opts[mode].map((o) => <SelectItem key={o.value} value={o.value} data-testid={`period-option-${o.value}`}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Input type="date" value={custom.start} onChange={(e) => setCustom((c) => ({ ...c, start: e.target.value }))} className="h-10 w-[150px] rounded-xl bg-card" data-testid="period-custom-start" />
          <span className="text-muted-foreground text-sm">to</span>
          <Input type="date" value={custom.end} onChange={(e) => setCustom((c) => ({ ...c, end: e.target.value }))} className="h-10 w-[150px] rounded-xl bg-card" data-testid="period-custom-end" />
          <Button onClick={applyCustom} className="h-10 rounded-xl" data-testid="period-custom-apply">Apply</Button>
        </div>
      )}
      {error && <div className="w-full text-xs text-rose-700" data-testid="period-error">{error}</div>}
    </div>
  );
}
